import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';

/**
 * Выпадашка, привязанная к кнопке. Рисуется порталом в body с
 * position: fixed — иначе её обрезает любой родитель с overflow,
 * а таких в шапке хватает.
 */
export function AnchoredPopover({
  anchorRef, open, onClose, align = 'start', children,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  align?: 'start' | 'end';
  children: React.ReactNode;
}) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const anchor = anchorRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const width = ref.current?.offsetWidth ?? 240;
    const height = ref.current?.offsetHeight ?? 240;

    let left = align === 'start' ? rect.left : rect.right - width;
    left = Math.max(8, Math.min(window.innerWidth - width - 8, left));

    // Не влезает снизу — показываем над кнопкой.
    const below = rect.bottom + 4;
    const top =
      below + height <= window.innerHeight - 8
        ? below
        : Math.max(8, Math.min(rect.top - 4 - height, window.innerHeight - height - 8));

    setPos({ top, left });
  }, [open, anchorRef, align]);

  useEffect(() => {
    if (!open) return;

    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!ref.current?.contains(target) && !anchorRef.current?.contains(target)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onResize = () => onClose();
    // Прокрутка любого предка — координаты уже не те, проще закрыть.
    const onScroll = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);

    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div ref={ref} style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 1000 }}>
      {children}
    </div>,
    document.body,
  );
}
